import { defineQuery } from "next-sanity";

const assetFragment = `
  asset->{
    _id,
    url,
    metadata {
      lqip,
      dimensions {
        width,
        height
      }
    }
  }
`;

const simpleAssetFragment = `
  asset->{
    _id,
    url,
    originalFilename
  }
`;

const imageWithAltFragment = `
  ${assetFragment},
  alt
`;

const speakerFields = `
  _key,
  name,
  jobTitle,
  company,
  linkedinUrl,
  image{
    ${imageWithAltFragment},
    hotspot
  }
`;

const ctaFragment = `
  _key,
  text,
  linkType,
  externalRoute,
  internalRoute,
  formOverlayTitle,
  formOverlayDescription,
  formOverlayImage{
    ${assetFragment}
  },
  formSuccessMessageTitle,
  formSuccessMessageDescription,
  formSubmitButtonText,
  formPostSubmitAction,
  formSuccessRedirectUrl,
  formPDF{
    ${simpleAssetFragment}
  }
`;

const baseSectionFields = `
  label,
  title,
  description,
  enableCTAField,
  ctas[]{
    ${ctaFragment}
  }
`;

const heroFields = `
  label,
  layout,
  title,
  personalizedTitle,
  description,
  hasCTAs,
  ctas[]{
    ${ctaFragment}
  },
  hasFeatures,
  features[]{
    _type,
    _key,
    text,
    hasGuaranteeDialog,
    _type == "sharedHeroTrustBar" => {
      "finCustomers": *[_id == "globalStatsSingleton"][0].finCustomers,
      "intercomCustomers": *[_id == "globalStatsSingleton"][0].intercomCustomers,
      "resolutionRate": *[_id == "globalStatsSingleton"][0].averageResolutionRate
    }
  },
  hasMedia,
  mediaType,
  riveLibraryKey,
  hideAnimationPlaceholder,
  image{
    ${imageWithAltFragment}
  },
  mobileImage{
    ${imageWithAltFragment}
  },
  backgroundImage{
    ${imageWithAltFragment}
  },
  backgroundPreset,
  testimonial->{
    _id,
    quote,
    name,
    jobTitle,
    portrait{
      ${imageWithAltFragment}
    },
    customer->{
      name,
      type,
      slug,
      logo{
        ${simpleAssetFragment}
      }
    }
  },
  testimonialStyle,
  hasStats,
  stats[]{
    label,
    value
  },
  hasForm,
  marketoFormId,
  formPostSubmitAction,
  formCTAText,
  formSuccessTitleText,
  formSuccessBodyText,
  formSuccessRedirectUrl,
  formAsset{
    ${simpleAssetFragment}
  },
  riveUpload{
    ${simpleAssetFragment}
  }
`;

const navigationFields = `
  enableCustomNavigation,
  enableLogoLink,
  ctas[]{
    ${ctaFragment}
  }
`;

const marqueeFields = `
  heading,
  showCompanyLogos,
  companyType,
  layout
`;

const footerCtaFields = `
  showFooterCta,
  label,
  title,
  ctas[]{
    ${ctaFragment}
  },
  theme,
  image{
    ${imageWithAltFragment}
  }
`;

const integrationsFields = `
  showIntegrations,
  enableCTAField,
  ctas[]{
    ${ctaFragment}
  }
`;

const pricingFields = `
  showPricing,
  variant
`;

const sectionExpansions = `
  _type == "sharedFeaturesHighlightSection" => {
    ...,
    slides[]{
      _key,
      title,
      description,
      media{
        image{
          ${assetFragment}
        }
      }
    }
  },
  _type == "sharedTestimonialsSection" => {
    ...,
    testimonials[]{
      _key,
      ...@->{
        ...,
        customer->{...}
      }
    }
  },
  _type == "sharedTestimonialsSectionV2" => {
    ...,
    ctas[]{
      ${ctaFragment}
    },
    testimonials[]{
      _key,
      ...@->{
        ...,
        portrait{
          ${imageWithAltFragment}
        },
        customer->{
          name,
          type,
          slug,
          logo{
            ${simpleAssetFragment}
          }
        }
      }
    }
  },
  _type == "performanceGraphsSection" => {...},
  _type == "sharedPerformanceGraphsSectionV2" => {
    ...,
    ctas[]{
      ${ctaFragment}
    },
    lineChartLabel,
    lineChartDescription,
    barChartLabel,
    barChartDescription
  },
  // sharedPricingSectionV2 is fixed-copy (fully hardcoded per-app in the
  // registry's format fn), so it needs no case here — the spread above
  // already carries its locale field through.
  _type == "sharedCalloutCardSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    }
  },
  _type == "sharedCsatBenchmarkSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    }
  },
  _type == "sharedRoiCalculatorSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    }
  },
  _type == "sharedSpeakersSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    },
    speakers[]{
      ${speakerFields}
    }
  },
  _type == "sharedImageTextColumnsSection" => {
    ...,
    items[]{
      title,
      description,
      image{
        ${imageWithAltFragment}
      },
      hasLink,
      link{
        text,
        href
      }
    }
  },
  _type == "sharedComparisonTableSection" => {
    ...,
    comparisonRows[]{...}
  },
  _type == "sharedComparisonTableSectionV2" => {
    ...,
    ctas[]{
      ${ctaFragment}
    },
    comparisonRows[]{...}
  },
  _type == "sharedTextColumnsWithGraphOrImageSection" => {
    ...,
    features[]{
      label,
      title,
      primaryContent,
      secondaryContent
    },
    images{
      hasImages,
      desktop{
        ${imageWithAltFragment}
      },
      mobile{
        ${imageWithAltFragment}
      }
    },
    barChart{
      hasBarChart,
      label,
      data[]{
        label,
        percentage
      }
    }
  },
  _type == "sharedStatsSection" => {
    ...,
    stats[]{...}
  },
  _type == "sharedStatisticsV2Section" => {
    ...,
    stats[]{...}
  },
  _type == "sharedBenefitCardsSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    },
    cards[]{...}
  },
  _type == "sharedProductFeaturesSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    },
    image{
      ${imageWithAltFragment}
    },
    cards[]{
      ...,
      image{
        ${imageWithAltFragment}
      }
    }
  },
  _type == "sharedFaqSection" => {
    ...,
    faqs[]{
      _key,
      question,
      answer
    }
  },
  _type == "sharedCtaBannerBandSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    }
  },
  _type == "sharedFinDemoSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    }
  },
  // sharedFinIntegrationsSection and sharedHelpdeskIntegrationsSection bake their
  // illustration into the component; copy and the CTA are editable (copy scalars
  // come through the spread; ctas needs its own dereference).
  _type == "sharedFinIntegrationsSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    }
  },
  _type == "sharedHelpdeskIntegrationsSection" => {
    ...,
    ctas[]{
      ${ctaFragment}
    }
  },
  _type == "sharedVoiceSectionV2" => {
    ...,
    ctas[]{
      ${ctaFragment}
    }
  },
  _type == "sharedVideoSectionV2" => {
    ...,
    video->{
      videoSrc,
      videoTitle,
      title
    }
  },
  _type == "sharedFeatureSpotlightSection" => {
    ...,
    mediaPosition,
    mediaType,
    riveLibraryKey,
    image{
      ${imageWithAltFragment}
    },
    cards[]{...}
  },
  _type == "sharedCarouselSection" => {
    ...,
    description,
    enableCTAField,
    ctas[]{
      ${ctaFragment}
    },
    orientation,
    slides[]{
      _key,
      title,
      description,
      linkHref,
      smallImage{
        ${assetFragment},
        alt
      },
      largeImage{
        ${assetFragment},
        alt
      },
      slideBackdrop{
        ${imageWithAltFragment}
      },
      slideBackdropPreset
    }
  },
  _type == "sharedCarouselSectionV2" => {
    ...,
    description,
    enableCTAField,
    ctas[]{
      ${ctaFragment}
    },
    orientation,
    slides[]{
      _key,
      title,
      description,
      linkHref,
      smallImage{
        ${assetFragment},
        alt
      },
      largeImage{
        ${assetFragment},
        alt
      },
      slideBackdrop{
        ${imageWithAltFragment}
      },
      slideBackdropPreset
    }
  },
  _type == "sharedG2BadgesSection" => {
    ...,
    badges[]->{
      _id,
      name,
      badge{
        ${simpleAssetFragment}
      }
    }
  },
  _type == "sharedG2BadgesSectionV2" => {
    ...,
    badges[]->{
      _id,
      name,
      badge{
        ${simpleAssetFragment}
      }
    }
  }
`;

export const drlpPageQuery = defineQuery(`
*[_type == "drlpPage" && slug.current == $slug && site == $site][0]{
  _id,
  slug,
  isLive,
  site,
  pageMetadata{
    title,
    description,
    path,
    jsonLd,
    socialImage{
      ${assetFragment}
    }
  },
  enablePersonalization,
  experimentFlagKey,
  marqueeSection{
    ${marqueeFields},
    enableAbTest,
    abTestVariations[]{
      ${marqueeFields}
    }
  },
  footerCtaSection{
    ${footerCtaFields},
    enableAbTest,
    abTestVariations[]{
      ${footerCtaFields}
    }
  },
  navigationGroup{
    ${navigationFields},
    enableAbTest,
    abTestVariations[]{
      ${navigationFields}
    }
  },
  integrationsSection{
    ${integrationsFields},
    enableAbTest,
    abTestVariations[]{
      ${integrationsFields}
    }
  },
  pricingSection{
    ${pricingFields},
    enableAbTest,
    abTestVariations[]{
      ${pricingFields}
    }
  },
  heroSection{
    ${heroFields},
    enableAbTest,
    abTestVariations[]{
      ${heroFields}
    }
  },
  sections[]{
    _type,
    _key,

    _type == "sharedFeaturesHighlightSection" => {
      ${baseSectionFields},
      wideMedia,
      slides[]{
        _key,
        title,
        description,
        media{
          image{
            ${assetFragment}
          },
          alt
        },
      }
    },

    _type == "sharedTestimonialsSection" => {
      ${baseSectionFields},
      hideLabels,
      testimonials[]{
        _key,
        ...@->{
          _id,
          quote,
          name,
          jobTitle,
          customer->{
            name,
            type,
            slug,
            logo{
              ${simpleAssetFragment}
            }
          }
        }
      }
    },

    _type == "sharedTestimonialsSectionV2" => {
      horizontalHeader,
      label,
      title,
      description,
      enableCTAField,
      hideHeadshot,
      ctas[]{
        ${ctaFragment}
      },
      testimonials[]{
        _key,
        ...@->{
          _id,
          quote,
          name,
          jobTitle,
          portrait{
            ${imageWithAltFragment}
          },
          customer->{
            name,
            type,
            slug,
            logo{
              ${simpleAssetFragment}
            }
          }
        }
      }
    },

    _type == "performanceGraphsSection" => {
      ${baseSectionFields},
      lineChartLabel,
      barChartLabel,
      barChartDescription
    },

    _type == "sharedPerformanceGraphsSectionV2" => {
      ${baseSectionFields},
      horizontalHeader,
      lineChartLabel,
      lineChartDescription,
      barChartLabel,
      barChartDescription
    },

    // sharedPricingSectionV2 is fixed-copy (fully hardcoded per-app in the
    // registry's format fn) aside from the locale picker below.
    _type == "sharedPricingSectionV2" => {
      locale
    },

    _type == "sharedImageTextColumnsSection" => {
      ${baseSectionFields},
      items[]{
        title,
        description,
        image{
          ${imageWithAltFragment}
        },
        hasLink,
        link{
          text,
          href
        }
      }
    },

    _type == "sharedComparisonTableSection" => {
      ${baseSectionFields},
      columnHeaders{
        ourProduct,
        competitor
      },
      comparisonRows[]{
        category,
        ourValue,
        competitorValue
      }
    },

    _type == "sharedComparisonTableSectionV2" => {
      ${baseSectionFields},
      columnHeaders{
        ourProduct,
        competitor
      },
      comparisonRows[]{
        category,
        ourValue,
        competitorValue
      }
    },

    _type == "sharedTextColumnsWithGraphOrImageSection" => {
      ${baseSectionFields},
      features[]{
        label,
        title,
        primaryContent,
        secondaryContent
      },
      images{
        hasImages,
        desktop{
          ${imageWithAltFragment}
        },
        mobile{
          ${imageWithAltFragment}
        }
      },
      barChart{
        hasBarChart,
        label,
        description
      },
      lineGraph{
        hasLineGraph,
        label
      }
    },

    _type == "sharedStatsSection" => {
      ${baseSectionFields},
      stats[]{
        label,
        value,
        description
      }
    },

    _type == "sharedStatisticsV2Section" => {
      ${baseSectionFields},
      horizontalHeader,
      stats[]{
        label,
        value,
        description
      }
    },

    _type == "sharedCsatBenchmarkSection" => {
      ${baseSectionFields},
      horizontalHeader
    },

    _type == "sharedRoiCalculatorSection" => {
      ${baseSectionFields},
      horizontalHeader,
      defaultInputs
    },

    _type == "sharedSpeakersSection" => {
      ${baseSectionFields},
      horizontalHeader,
      speakers[]{
        ${speakerFields}
      }
    },

    _type == "sharedBenefitCardsSection" => {
      ${baseSectionFields},
      horizontalHeader,
      cards[]{
        title,
        description
      }
    },

    _type == "sharedProductFeaturesSection" => {
      ${baseSectionFields},
      horizontalHeader,
      layout,
      image{
        ${imageWithAltFragment}
      },
      cards[]{
        _key,
        title,
        description,
        image{
          ${imageWithAltFragment}
        }
      }
    },

    _type == "sharedFaqSection" => {
      label,
      title,
      faqs[]{
        _key,
        question,
        answer
      }
    },

    _type == "sharedCtaBannerBandSection" => {
      label,
      title,
      description,
      ctas[]{
        ${ctaFragment}
      }
    },

    _type == "sharedFinDemoSection" => {
      label,
      title,
      description,
      ctas[]{
        ${ctaFragment}
      }
    },

    // sharedFinIntegrationsSection and sharedHelpdeskIntegrationsSection bake
    // their illustration into the component; copy and the CTA are editable.
    _type == "sharedFinIntegrationsSection" => {
      label,
      title,
      description,
      items,
      illustrationAlt,
      ctas[]{
        ${ctaFragment}
      }
    },

    _type == "sharedHelpdeskIntegrationsSection" => {
      label,
      title,
      description,
      items,
      illustrationAlt,
      ctas[]{
        ${ctaFragment}
      }
    },

    _type == "sharedVoiceSectionV2" => {
      label,
      title,
      description,
      cardTitle,
      cardBody,
      ctas[]{
        ${ctaFragment}
      }
    },

    _type == "sharedVideoSectionV2" => {
      label,
      title,
      description,
      video->{
        videoSrc,
        videoTitle,
        title
      }
    },

    _type == "sharedCalloutCardSection" => {
      title,
      description,
      ctas[]{
        ${ctaFragment}
      }
    },

    _type == "sharedFeatureSpotlightSection" => {
      ${baseSectionFields},
      mediaPosition,
      mediaType,
      riveLibraryKey,
      image{
        ${imageWithAltFragment}
      },
      cards[]{
        title,
        description
      }
    },

    _type == "sharedCarouselSection" => {
      ${baseSectionFields},
      orientation,
      slides[]{
        _key,
        title,
        description,
        linkHref,
        smallImage{
          ${assetFragment},
          alt
        },
        largeImage{
          ${assetFragment},
          alt
        },
        slideBackdrop{
          ${imageWithAltFragment}
        },
        slideBackdropPreset
      }
    },

    _type == "sharedCarouselSectionV2" => {
      ${baseSectionFields},
      horizontalHeader,
      orientation,
      slides[]{
        _key,
        title,
        description,
        linkHref,
        smallImage{
          ${assetFragment},
          alt
        },
        largeImage{
          ${assetFragment},
          alt
        },
        slideBackdrop{
          ${imageWithAltFragment}
        },
        slideBackdropPreset
      }
    },

    // No baseSectionFields here: this section has only title + badges
    // (no label/description/ctas).
    _type == "sharedG2BadgesSection" => {
      title,
      badges[]->{
        _id,
        name,
        badge{
          ${simpleAssetFragment}
        }
      }
    },

    _type == "sharedG2BadgesSectionV2" => {
      title,
      badges[]->{
        _id,
        name,
        badge{
          ${simpleAssetFragment}
        }
      }
    },

    _type in ["sharedAbTestSection", "finAbTestSection"] => {
      testName,
      enabled,
      control[]{
        _type,
        _key,
        ...,
        ${sectionExpansions}
      },
      variations[]{
        _type,
        _key,
        ...,
        ${sectionExpansions}
      },
      previewMode,
      notes
    },

  },
}`);
